import { useEstimationStore } from '../stores/estimationStore';
import { usePlanningStore } from '../stores/planningStore';
import { useFinanceStore } from '../stores/financeStore';

/**
 * Collects the current analysis state from all three stores
 * into a payload suitable for the snapshot API.
 */
export function collectSnapshotData() {
  const est = useEstimationStore.getState();
  const plan = usePlanningStore.getState();
  const fin = useFinanceStore.getState();

  const estimation_data: Record<string, unknown> = {
    inputs: {
      ha: est.ha,
      haZi: est.haZi,
      niveau: est.niveau,
      n2Commune: est.n2Commune,
      n2EcoleCT: est.n2EcoleCT,
    },
    result: est.result,
    r2: est.r2,
    // New v9 features
    paChEnabled: est.paChEnabled,
    paGeEnabled: est.paGeEnabled,
    customSubsidies: est.customSubsidies,
    programmeDetail: est.programmeDetail,
    planning: {
      planSteps: plan.planSteps,
      planAssign: plan.planAssign,
      popOverrides: plan.popOverrides,
      costOverrides: plan.costOverrides,
      nextId: plan.nextId,
    },
    finance: {
      exploitation: fin.exploitation,
      useForfait: fin.useForfait,
      forfait: fin.forfait,
      revenus: fin.revenus,
      emploisOverride: fin.emploisOverride,
      chgGrowth: fin.chgGrowth,
      revGrowth: fin.revGrowth,
      centimes: fin.centimes,
      creditMontant: fin.creditMontant,
      creditInteret: fin.creditInteret,
      surfaceVoirie: fin.surfaceVoirie,
      classesExtension: fin.classesExtension,
      abrisPlaces: fin.abrisPlaces,
      partNRmix: fin.partNRmix,
      partNRzi: fin.partNRzi,
    },
  };

  const n2_overrides: Record<string, number> = { ...est.n2Pct };

  return { estimation_data, n2_overrides };
}

/**
 * Restores snapshot data into the three stores.
 * Backward-compatible: gracefully handles old snapshots
 * that lack planning/finance sub-keys.
 */
export function restoreSnapshotData(
  estimation_data: Record<string, unknown>,
  n2_overrides: Record<string, number> | null,
) {
  const estHydrate = useEstimationStore.getState().hydrate;
  const planHydrate = usePlanningStore.getState().hydrate;
  const finHydrate = useFinanceStore.getState().hydrate;

  // Restore estimation inputs
  const inputs = estimation_data.inputs as Record<string, unknown> | undefined;
  if (inputs) {
    estHydrate({
      ha: inputs.ha as number,
      haZi: inputs.haZi as number,
      niveau: inputs.niveau as 1 | 2,
      n2Commune: (inputs.n2Commune as string) ?? '',
      n2EcoleCT: (inputs.n2EcoleCT as boolean) ?? false,
    });
  }

  // Restore estimation results
  if (estimation_data.result !== undefined) {
    estHydrate({ result: estimation_data.result as any });
  }
  if (estimation_data.r2 !== undefined) {
    estHydrate({ r2: estimation_data.r2 as any });
  }

  // Restore N2 overrides
  if (n2_overrides) {
    estHydrate({ n2Pct: { ...n2_overrides } });
  }

  // Restore v9 features (if present — old snapshots won't have these)
  if (estimation_data.paChEnabled !== undefined) {
    estHydrate({ paChEnabled: estimation_data.paChEnabled as boolean });
  }
  if (estimation_data.paGeEnabled !== undefined) {
    estHydrate({ paGeEnabled: estimation_data.paGeEnabled as boolean });
  }
  if (estimation_data.customSubsidies !== undefined) {
    estHydrate({ customSubsidies: estimation_data.customSubsidies as any });
  }
  if (estimation_data.programmeDetail !== undefined) {
    estHydrate({ programmeDetail: estimation_data.programmeDetail as any });
  }

  // Restore planning (if present — old snapshots won't have this)
  const planning = estimation_data.planning as Record<string, unknown> | undefined;
  if (planning) {
    planHydrate({
      planSteps: planning.planSteps as any,
      planAssign: planning.planAssign as any,
      popOverrides: planning.popOverrides as any,
      costOverrides: (planning.costOverrides as any) ?? {},
      nextId: planning.nextId as number,
    });
  }

  // Restore finance (if present — old snapshots won't have this)
  const finance = estimation_data.finance as Record<string, unknown> | undefined;
  if (finance) {
    finHydrate({
      exploitation: finance.exploitation as any,
      useForfait: finance.useForfait as boolean,
      forfait: finance.forfait as number,
      revenus: finance.revenus as any,
      emploisOverride: finance.emploisOverride as number | null,
      chgGrowth: finance.chgGrowth as number,
      revGrowth: finance.revGrowth as number,
      centimes: finance.centimes as number,
      creditMontant: finance.creditMontant as number,
      creditInteret: finance.creditInteret as number,
      surfaceVoirie: finance.surfaceVoirie as number,
      classesExtension: finance.classesExtension as number,
      abrisPlaces: finance.abrisPlaces as number,
      partNRmix: (finance.partNRmix as number | undefined) ?? 50,
      partNRzi: (finance.partNRzi as number | undefined) ?? 80,
    });
  }
}
